import { config } from "_config";
import { saveFile } from "_helpers";
import { authHeader, customerUid, getApiProxyUrl, handleResponse, mappingQueryParams } from "_helpers";

const { customerApiUrl, ordersApiUrl } = config;

export const ordersService = {
  getOrders,
  crmGetOrders,
  getOrder,
  crmGetOrder,
  putProduct,
  crmPutProduct,
  getFile,
  crmGetFile,
  getFileFromProduct,
  crmGetFileFromProduct,
  postFile,
  crmPostFile,
  createDelayedUpsell,
  getBlobFileFromProduct,
  customerAction,
  chargeExtensionFee,
  chargeStatementOfUseFee,
  chargeUSPTOFee,
  feeRecharge,
  chargeRevivalFee,
  getSubscriptionDetails,
};

function getOrders(params) {
  const requestOptions = {
    method: "GET",
    headers: authHeader(),
  };

  const url = `${customerApiUrl}/customers/${customerUid()}/orders${mappingQueryParams(params)}`;

  return fetch(url, requestOptions).then(handleResponse);
}

function crmGetOrders(params) {
  const requestOptions = {
    method: "GET",
    headers: authHeader(),
  };

  const url = `${getApiProxyUrl()}/customers/${customerUid()}/orders${mappingQueryParams(params)}`;

  return fetch(url, requestOptions).then(handleResponse);
}

function getOrder(orderId) {
  const requestOptions = {
    method: "GET",
    headers: authHeader(),
  };

  const url = `${customerApiUrl}/customers/${customerUid()}/orders/${orderId}`;

  return fetch(url, requestOptions).then(handleResponse);
}

function crmGetOrder(orderId) {
  const requestOptions = {
    method: "GET",
    headers: authHeader(),
  };

  const url = `${getApiProxyUrl()}/customers/${customerUid()}/orders/${orderId}`;

  return fetch(url, requestOptions).then(handleResponse);
}

function getSubscriptionDetails({ orderId, productId }) {
  const requestOptions = {
    method: "GET",
    headers: authHeader(),
  };

  const url = `${ordersApiUrl}/orders/${orderId}/products/${productId}/subscription-details`;

  return fetch(url, requestOptions).then(handleResponse);
}

function putProduct(productId, product, isRawResponse) {
  const requestOptions = {
    method: "PUT",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify(product),
  };

  const url = `${customerApiUrl}/customers/${customerUid()}/products/${productId}`;

  if (isRawResponse) {
    return fetch(url, requestOptions);
  }

  return fetch(url, requestOptions).then(handleResponse);
}

function crmPutProduct(productId, product) {
  const requestOptions = {
    method: "PUT",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify(product),
  };

  const url = `${getApiProxyUrl()}/customers/${customerUid()}/products/${productId}`;

  return fetch(url, requestOptions).then(handleResponse);
}

function getFile(orderId, fileId, useNameFromHeader) {
  const requestOptions = {
    method: "GET",
    headers: authHeader(),
  };

  const url = `${ordersApiUrl}/orders/${orderId}/file/${fileId}`;

  return fetch(url, requestOptions).then((response) => {
    if (response?.status === 200) {
      saveFile(response, fileId, useNameFromHeader);
    } else {
      return handleResponse(response);
    }
  });
}

function crmGetFile(orderId, fileId, useNameFromHeader) {
  const requestOptions = {
    method: "GET",
    headers: authHeader(),
  };

  const url = `${getApiProxyUrl()}/orders/${orderId}/file/${fileId}`;

  return fetch(url, requestOptions).then((response) => {
    if (response?.status === 200) {
      saveFile(response, fileId, useNameFromHeader);
    } else {
      return handleResponse(response);
    }
  });
}

function getFileFromProduct(orderId, productId, fileType, useNameFromHeader) {
  const requestOptions = {
    method: "GET",
    headers: authHeader(),
  };

  const url = `${ordersApiUrl}/orders/${orderId}/products/${productId}/result-PDF${mappingQueryParams({ fileType })}`;

  return fetch(url, requestOptions).then((response) => {
    if (response?.status === 200) {
      saveFile(response, fileType, useNameFromHeader);
    } else {
      return handleResponse(response);
    }
  });
}

function crmGetFileFromProduct(orderId, productId, fileType, useNameFromHeader) {
  const requestOptions = {
    method: "GET",
    headers: authHeader(),
  };

  const url = `${getApiProxyUrl()}/orders/${orderId}/products/${productId}/result-PDF${mappingQueryParams({
    fileType,
  })}`;

  return fetch(url, requestOptions).then((response) => {
    if (response?.status === 200) {
      saveFile(response, fileType, useNameFromHeader);
    } else {
      return handleResponse(response);
    }
  });
}

function getBlobFileFromProduct(orderId, productId, fileType) {
  const requestOptions = {
    method: "GET",
    headers: authHeader(),
  };

  const url = `${ordersApiUrl}/orders/${orderId}/products/${productId}/result-PDF${mappingQueryParams({ fileType })}`;

  return fetch(url, requestOptions).then((response) => {
    if (!response.ok) {
      return Promise.reject("An error occurred while downloading the file");
    }

    return Promise.resolve(response);
  });
}

function postFile(orderId, productId, formData, fileType, isRawResponse) {
  const requestOptions = {
    method: "POST",
    headers: authHeader(),
    body: formData,
  };

  const url = `${ordersApiUrl}/orders/${orderId}/products/${productId}/result-PDF${mappingQueryParams({ fileType })}`;

  if (isRawResponse) {
    return fetch(url, requestOptions);
  }

  return fetch(url, requestOptions).then(handleResponse);
}

function crmPostFile(orderId, productId, formData, fileType) {
  const requestOptions = {
    method: "POST",
    headers: authHeader(),
    body: formData,
  };

  const url = `${getApiProxyUrl()}/orders/${orderId}/products/${productId}/result-PDF${mappingQueryParams({
    fileType,
  })}`;

  return fetch(url, requestOptions).then(handleResponse);
}

function createDelayedUpsell(orderId, body, isRawResponse) {
  const requestOptions = {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };

  const url = `${ordersApiUrl}/orders/${orderId}/create-delayed-upsale`;

  if (isRawResponse) {
    return fetch(url, requestOptions);
  }

  return fetch(url, requestOptions).then(handleResponse);
}

function customerAction({ productId, actionType, message }) {
  const requestOptions = {
    method: "POST",
    headers: authHeader(),
  };

  const url = `${customerApiUrl}/customers/${customerUid()}/products/${productId}/action${mappingQueryParams({
    actionType,
    message,
  })}`;

  return fetch(url, requestOptions).then(handleResponse);
}

function chargeUSPTOFee({ orderId }) {
  const requestOptions = {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
  };

  let url = `${ordersApiUrl}/orders/${orderId}/charge-uspto-fee`;

  return fetch(url, requestOptions).then((response) => handleResponse(response, { allowForbidden: true }));
}

function feeRecharge({ orderId, productId }) {
  const requestOptions = {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
  };

  let url = `${ordersApiUrl}/orders/${orderId}/products/${productId}/fee-recharge`;

  return fetch(url, requestOptions).then((response) => handleResponse(response, { allowForbidden: true }));
}

function chargeExtensionFee(orderId, classes, isRawResponse) {
  const requestOptions = {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
  };

  let url = `${ordersApiUrl}/orders/${orderId}/charge-extension-fee`;

  if (classes && classes > 0) {
    url += mappingQueryParams({ classes });
  }

  if (isRawResponse) {
    return fetch(url, requestOptions);
  }

  return fetch(url, requestOptions).then(handleResponse);
}

function chargeStatementOfUseFee(orderId, classes, isRawResponse) {
  const requestOptions = {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
  };

  let url = `${ordersApiUrl}/orders/${orderId}/charge-sou-fee`;

  if (classes && classes > 0) {
    url += mappingQueryParams({ classes });
  }

  if (isRawResponse) {
    return fetch(url, requestOptions);
  }

  return fetch(url, requestOptions).then(handleResponse);
}

function chargeRevivalFee(orderId, classes, isRawResponse) {
  const requestOptions = {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
  };

  let url = `${ordersApiUrl}/orders/${orderId}/charge-revival-fee`;

  if (classes && classes > 0) {
    url += mappingQueryParams({ classes });
  }

  if (isRawResponse) {
    return fetch(url, requestOptions);
  }

  return fetch(url, requestOptions).then((response) => handleResponse(response, { allowForbidden: true }));
}
